/**
 * SOLID Review
 * S — Good. The auth/account/payment REST surface as Infrastructure speaks it: register, referral
 *     validation, email/login-code verification, checkout-session creation, profile read/update and email
 *     change — ten calls over one `post()`/`get()` pair, one concern (HTTP to `/api/auth/*` and
 *     `/api/stripe/*`). Cohesive but wide.
 * O — Good. A new endpoint is one more method here; nothing existing is touched.
 * L — N/A — no inheritance hierarchy (plain object literals, not classes).
 * I — Acceptable. `IGatewayAuthRest` is wide by nature: the auth REST API is ten operations. Its two
 *     consumers split it: `ClientAuthServerAdapter` the account's eight, `ClientPaymentServerAdapter` the
 *     two payment calls (FE4c — the Framework held those as `IAdapterAuthApi` until then).
 * D — Good. The REST client port and the wire contract.
 */
import { auth as authContract, payment as paymentContract } from '@ml3d/contract/rest';
import type { ZodType } from 'zod';
import type { IPortRestApiClient } from '../contract/ports';

/** One request's transport options, annotated so a typo in a key is a compile error. */
interface IConfigAuthRequest {
  readonly method: string;
  readonly body?: unknown;
  readonly jwt?: string | null;
}

// API_BASE now comes from infrastructure/api/apiBase.ts — it was declared here, in
// LegalApiAdapter and in ConsentApiAdapter, and the third read import.meta.env directly.

export type IEntityRegister = authContract.TRegisterResponse;

export type IEntityAuthSuccess = authContract.TAuthSuccessResponse;

export type IEntityGenericMessage = authContract.TGenericMessageResponse;

export type IEntityCheckoutSession = paymentContract.TCheckoutSessionResponse;

export type IEntityProfile = authContract.TProfileResponse;

export type IEntityMe = authContract.TMeResponse;

export interface IEntityApi {
  error: string;
}

// ---------------------------------------------------------------------------
// REQUEST BODIES (§28.60)
// ---------------------------------------------------------------------------
// `post` declares `body: unknown`, so until these existed nothing checked a single
// request literal -- a typo'd key would have been serialised and sent, and the
// backend route would have read undefined. Annotating the const restores the check
// at the declaration even though `post`'s own parameter stays `unknown`.
//
// These are the client half of the contracts in backend `framework/http/routes/authRoutes.ts`.
// Nothing mechanically ties the two halves together; that is a gap worth closing, and it is
// exactly what the e2e contract tier exists for.

interface ICommandRegister {
  readonly email: string;
  readonly displayName: string;
  readonly referralCode?: string;
  readonly consentAccepted?: boolean;
}

interface ICommandVerifyEmail {
  readonly email: string;
  readonly code: string;
}

interface ICommandRequestLoginCode {
  readonly email: string;
}

interface ICommandVerifyLoginCode {
  readonly email: string;
  readonly code: string;
}

/**
 * Intentionally empty: the route takes its player from the bearer token, not the body.
 * An empty interface is not a no-op here -- excess-property checking still fires against
 * it, so adding a stray field to this call becomes a compile error rather than a silently
 * ignored payload.
 */
type ICommandCreateCheckoutSession = Record<string, never>;

interface ICommandUpdateDisplayName {
  readonly displayName: string;
}

interface ICommandRequestEmailChange {
  readonly email: string;
}

interface ICommandVerifyEmailChange {
  readonly email: string;
  readonly code: string;
}

/**
 * THE AUTH ADAPTER, BUILT FROM A SOCKET (2026-08-30).
 *
 * This module used to be a bare object literal calling `fetch(`${API_BASE}${path}`)` eleven times.
 * Meanwhile `ComponentFrontendInfrastructure.restAuthApiPort` — the socket that pairs with the
 * backend's RestAuthApi lollipop — was connected by `connect()` and NEVER READ. assertAssembled
 * passed because it was wired; the system diagram paired it by name; and the traffic went out
 * through raw fetch beside it. That is the exact shape the kernel's new read flag now detects.
 *
 * `IPortRestApiClient.request(path, init, parse)` already took the contract's parse function and
 * `RestApiClient` already implemented 204 handling, bearer auth and the error shape — so this is
 * mostly deletion. The two hand-rolled fetches (validateReferral, getMe) collapse into it too,
 * and getMe gains the error handling `post()` had and it did not.
 */
/**
 * THE AUTH REST SURFACE, AS INFRASTRUCTURE SPEAKS IT (frontend strict-ports plan, FE3, 2026-10-03): the
 * wire's shapes, and the token passed per call. It was `application/ports/IAdapterAuthApi.ts`, handed to
 * the Framework, where seven components held the JWT to pass it in. Since FE3 its consumer is
 * `ClientAuthServerAdapter`, which keeps the token, and the interface lives beside its implementation —
 * an Infrastructure-internal seam, not a port.
 */
export interface IGatewayAuthRest {
  register(email: string, displayName: string, referralCode?: string, consentAccepted?: boolean): Promise<IEntityRegister>;
  validateReferral(code: string): Promise<{ valid: boolean }>;
  verifyEmail(email: string, code: string): Promise<IEntityAuthSuccess>;
  requestLoginCode(email: string): Promise<IEntityGenericMessage>;
  verifyLoginCode(email: string, code: string): Promise<IEntityAuthSuccess>;
  createCheckoutSession(jwt: string): Promise<IEntityCheckoutSession>;
  getMe(jwt: string): Promise<IEntityMe>;
  updateDisplayName(jwt: string, displayName: string): Promise<IEntityProfile>;
  requestEmailChange(jwt: string, newEmail: string): Promise<IEntityGenericMessage>;
  verifyEmailChange(jwt: string, newEmail: string, code: string): Promise<IEntityProfile>;
}

export function createAuthApiAdapter(client: IPortRestApiClient): IGatewayAuthRest {
  const post = <T>(path: string, body: unknown, schema: ZodType<T>, jwt?: string | null): Promise<T> => {
    // NAMED AND ANNOTATED, not inline. `ops/check-options-literals.mjs` caps inline literals at our
    // own parameters at zero, and the annotation is the part that matters: excess-property checking
    // fires only on a FRESH literal, so an unannotated const would be strictly worse than the
    // inline form it replaced (§28.60).
    const requestPost: IConfigAuthRequest = { method: 'POST', body, ...(jwt === undefined ? {} : { jwt }) };
    return client.request(path, requestPost, (d) => schema.parse(d));
  };
  const get = <T>(path: string, schema: ZodType<T>, jwt?: string | null): Promise<T> => {
    const requestGet: IConfigAuthRequest = { method: 'GET', ...(jwt === undefined ? {} : { jwt }) };
    return client.request(path, requestGet, (d) => schema.parse(d));
  };

  return {
  register(email: string, displayName: string, referralCode?: string, consentAccepted?: boolean): Promise<IEntityRegister> {
    const commandRegister: ICommandRegister = {
      email,
      displayName,
      ...(referralCode ? { referralCode } : {}),
      ...(consentAccepted ? { consentAccepted: true } : {}),
    };
    return post('/api/auth/register', commandRegister, authContract.RegisterResponse);
  },

  validateReferral(code: string): Promise<{ valid: boolean }> {
    return get(`/api/auth/validate-referral?code=${encodeURIComponent(code)}`, authContract.ValidateReferralResponse);
  },

  verifyEmail(email: string, code: string): Promise<IEntityAuthSuccess> {
    const commandVerifyEmail: ICommandVerifyEmail = { email, code };
    return post('/api/auth/verify-email', commandVerifyEmail, authContract.AuthSuccessResponse);
  },

  requestLoginCode(email: string): Promise<IEntityGenericMessage> {
    const commandRequestLoginCode: ICommandRequestLoginCode = { email };
    return post('/api/auth/request-login-code', commandRequestLoginCode, authContract.GenericMessageResponse);
  },

  verifyLoginCode(email: string, code: string): Promise<IEntityAuthSuccess> {
    const commandVerifyLoginCode: ICommandVerifyLoginCode = { email, code };
    return post('/api/auth/verify-login-code', commandVerifyLoginCode, authContract.AuthSuccessResponse);
  },

  createCheckoutSession(jwt: string): Promise<IEntityCheckoutSession> {
    const commandCreateCheckoutSession: ICommandCreateCheckoutSession = {};
    return post('/api/stripe/create-checkout-session', commandCreateCheckoutSession, paymentContract.CheckoutSessionResponse, jwt);
  },

  getMe(jwt: string): Promise<IEntityMe> {
    return get('/api/auth/me', authContract.MeResponse, jwt);
  },

  updateDisplayName(jwt: string, displayName: string): Promise<IEntityProfile> {
    const commandUpdateDisplayName: ICommandUpdateDisplayName = { displayName };
    return post('/api/auth/update-display-name', commandUpdateDisplayName, authContract.ProfileResponse, jwt);
  },

  requestEmailChange(jwt: string, newEmail: string): Promise<IEntityGenericMessage> {
    const commandRequestEmailChange: ICommandRequestEmailChange = { email: newEmail };
    return post('/api/auth/request-email-change', commandRequestEmailChange, authContract.GenericMessageResponse, jwt);
  },

  verifyEmailChange(jwt: string, newEmail: string, code: string): Promise<IEntityProfile> {
    const commandVerifyEmailChange: ICommandVerifyEmailChange = { email: newEmail, code };
    return post('/api/auth/verify-email-change', commandVerifyEmailChange, authContract.ProfileResponse, jwt);
  },
  };
}
